/**
 * PURPOSE:
 * Centralizar el catálogo de tabs públicos de SportSection y la resolución por tipo de deporte.
 *
 * RESPONSIBILITIES:
 * - Definir los códigos oficiales de tabs públicos.
 * - Construir los paneles correspondientes a cada tab.
 * - Mantener defaults fijos para colectivos/colectivos express.
 * - Permitir configuración por DB solo para deportes individuales.
 * - Preservar las condiciones legacy por evento/deporte solo para resolver la variante
 *   del contenido del tab habilitado, NO para activar tabs automáticamente.
 *
 * COLLABORATORS:
 * - src/sections/SportSection.jsx
 * - src/components públicos de Hayllis
 *
 * ROUTE:
 * src/utils/publicSportTabs.jsx
 */

import React from "react";

import Diversity3Icon from "@mui/icons-material/Diversity3";
import SettingsInputSvideoIcon from "@mui/icons-material/SettingsInputSvideo";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PollIcon from "@mui/icons-material/Poll";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import InfoIcon from "@mui/icons-material/Info";

import Equipos2 from "../components/Equipos/Equipos2";
import Fixture from "../components/Fixture/Fixture";
import BracketWrapper from "../components/Fixture/BracketWrapper";
import Event17PublicBracket from "../components/Fixture/components/Event17PublicBracket";
import Programacion from "../components/Programacion/Programacion";
import ProgramacionIndividual from "../components/ProgramacionIndividual/ProgramacionIndividual";
import Pruebas from "../components/Pruebas/Pruebas.jsx";
import Categorias from "../components/Categorias/Categorias";
import TablaFinal from "../components/TablaFinal/TablaFinal.jsx";
import Resultados from "../components/Resultados/Resultados";
import Estadistica from "../components/Estadistica/Estadistica";
import Registrados from "../components/Registrados/Registrados";
import Informacion from "../components/Informacion/Informacion";
import ResultadosDoc from "../components/ResultadosDoc/ResultadosDoc";
import Medallero from "../components/Medallero/Medallero";
import CategoriasKarate from "../components/Software/Karate/Categorias/CategoriasKarate.jsx";

export const KARATE_HAYLLIS_EVENT_ID = 223;
export const KARATE_HAYLLIS_SPORT_ID = 10;

export const SPORT_TYPES = Object.freeze({
  COLECTIVO: 1,
  COLECTIVO_EXPRESS: 2,
  INDIVIDUAL: 3,
});

export const PUBLIC_TAB_CODES = Object.freeze({
  EQUIPOS: "1",
  FIXTURE: "2",
  PROGRAMACION: "3",
  RESULTADOS: "4",
  ESTADISTICA: "5",
  INFORMACION: "6",
  PRUEBAS: "7",
  CATEGORIAS: "8",
  TABLA_FINAL: "9",
  MEDALLERO: "10",
});

export const DEFAULT_PUBLIC_TAB_CODES = Object.freeze({
  COLECTIVO: [
    PUBLIC_TAB_CODES.EQUIPOS,
    PUBLIC_TAB_CODES.FIXTURE,
    PUBLIC_TAB_CODES.PROGRAMACION,
    PUBLIC_TAB_CODES.RESULTADOS,
    PUBLIC_TAB_CODES.ESTADISTICA,
  ],
  COLECTIVO_EXPRESS: [
    PUBLIC_TAB_CODES.EQUIPOS,
    PUBLIC_TAB_CODES.PROGRAMACION,
    PUBLIC_TAB_CODES.RESULTADOS,
    PUBLIC_TAB_CODES.ESTADISTICA,
  ],
  INDIVIDUAL: [
    PUBLIC_TAB_CODES.EQUIPOS,
    PUBLIC_TAB_CODES.INFORMACION,
    PUBLIC_TAB_CODES.PROGRAMACION,
    PUBLIC_TAB_CODES.RESULTADOS,
  ],
});

const ALL_PUBLIC_TAB_CODES = new Set(Object.values(PUBLIC_TAB_CODES));

/** Determina si la sección pública debe usar el módulo público optimizado de KarateSoft. */
export const isKarateHayllisSection = (ideventNumber, idsportNumber) => {
  return (
    Number(ideventNumber) === KARATE_HAYLLIS_EVENT_ID &&
    Number(idsportNumber) === KARATE_HAYLLIS_SPORT_ID
  );
};

/**
 * Condiciones legacy que antes vivían dentro de SportSection.jsx.
 *
 * IMPORTANTE:
 * - Estas condiciones NO deciden qué tabs son visibles.
 * - La visibilidad/orden de individuales la decide config_category.tabs_config_publico.
 * - Estas condiciones solo sirven para que, si un tab fue habilitado desde el panel,
 *   renderice la variante correcta del contenido para ese deporte/evento.
 */
export const getLegacyIndividualTabContext = (ideventNumber, idsportNumber) => {
  const idevent = Number(ideventNumber);
  const idsport = Number(idsportNumber);

  const isAthleticsSpecial = idevent === 223 && idsport === 2;
  const isSwimmingSpecial = idevent === 223 && idsport === 11;
  const isJudoSpecial = idevent === 223 && idsport === 8;
  const isWrestlingSpecial = idevent === 223 && idsport === 25;
  const isKarateSpecial = isKarateHayllisSection(idevent, idsport);
  const isLimited80 = idevent === 80;

  return {
    isAthleticsSpecial,
    isSwimmingSpecial,
    isJudoSpecial,
    isWrestlingSpecial,
    isKarateSpecial,
    isLimited80,
    hasPruebasLegacyFlow: isAthleticsSpecial || isSwimmingSpecial,
    hasCategoriasLegacyFlow: isJudoSpecial || isWrestlingSpecial || isKarateSpecial,
    hasTablaFinalLegacyFlow:
      isAthleticsSpecial || isSwimmingSpecial || isJudoSpecial || isWrestlingSpecial,
  };
};

/** Renderiza el contenido reutilizable del tab Equipos. */
const renderEquiposPanel = ({ idinstitution, handleInstitutionClick }) => {
  if (idinstitution) {
    return <Registrados institutionId={idinstitution} />;
  }

  return <Equipos2 onInstitutionClick={handleInstitutionClick} />;
};

/**
 * Lee un string tipo "1,6,3,4" desde DB y lo normaliza.
 *
 * Reglas defensivas:
 * - Acepta espacios: "1, 6, 3, 4".
 * - Elimina duplicados preservando orden.
 * - Ignora códigos desconocidos.
 * - Si no queda ningún código válido, usa fallbackCodes.
 */
export const parseTabsConfigPublico = (rawConfig, fallbackCodes) => {
  const fallback = Array.isArray(fallbackCodes) ? fallbackCodes : [];

  const normalizedCodes = String(rawConfig || "")
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);

  const uniqueValidCodes = [];
  normalizedCodes.forEach((code) => {
    if (!ALL_PUBLIC_TAB_CODES.has(code)) {
      return;
    }

    if (uniqueValidCodes.includes(code)) {
      return;
    }

    uniqueValidCodes.push(code);
  });

  return uniqueValidCodes.length ? uniqueValidCodes : fallback;
};

/**
 * Resuelve el panel de Programación.
 *
 * Colectivos/express usan Programacion.
 * Individuales usan ProgramacionIndividual.
 */
const renderProgramacionPanel = ({ isColectivoLike }) => {
  return isColectivoLike ? <Programacion /> : <ProgramacionIndividual />;
};

/**
 * Resuelve el panel de Resultados.
 *
 * Colectivos/express usan Resultados.
 * Individuales usan ResultadosDoc.
 */
const renderResultadosPanel = ({ isColectivoLike }) => {
  return isColectivoLike ? <Resultados /> : <ResultadosDoc />;
};

/**
 * Resuelve el panel de Pruebas.
 *
 * Antes este tab aparecía hardcodeado en Atletismo/Natación especiales.
 * Ahora solo aparece si el panel administrativo lo habilita con código 7.
 * Cuando aparece, se mantiene el mismo componente legacy con idsport.
 */
const renderPruebasPanel = ({ idsportNumber, legacyContext }) => {
  if (legacyContext.hasPruebasLegacyFlow) {
    return <Pruebas idsport={idsportNumber} />;
  }

  // Fallback seguro: permite configurar el tab en otros individuales sin romper la vista.
  return <Pruebas idsport={idsportNumber} />;
};

/**
 * Resuelve el panel de Categorías.
 *
 * Antes:
 * - Karate Hayllis 223/10 usaba CategoriasKarate.
 * - Judo/Lucha especiales usaban Categorias.
 * Ahora el tab solo aparece si el panel administrativo lo habilita con código 8,
 * pero mantiene la variante correcta de contenido.
 */
const renderCategoriasPanel = ({ ideventNumber, idsportNumber, legacyContext }) => {
  if (legacyContext.isKarateSpecial) {
    return <CategoriasKarate idevent={ideventNumber} idsport={idsportNumber} />;
  }

  return <Categorias idsport={idsportNumber} />;
};

/**
 * Resuelve el panel de Tabla Final.
 *
 * Antes este tab aparecía hardcodeado en Atletismo/Natación/Judo/Lucha especiales.
 * Ahora solo aparece si el panel administrativo lo habilita con código 9.
 */
const renderTablaFinalPanel = () => {
  return <TablaFinal />;
};

/** Construye un catálogo completo; luego cada tipo de deporte decide qué códigos consume. */
const buildPublicTabCatalog = ({
  sportType,
  ideventNumber,
  idsportNumber,
  idinstitution,
  handleInstitutionClick,
}) => {
  const isColectivoLike =
    Number(sportType) === SPORT_TYPES.COLECTIVO ||
    Number(sportType) === SPORT_TYPES.COLECTIVO_EXPRESS;
  const legacyContext = getLegacyIndividualTabContext(ideventNumber, idsportNumber);

  return {
    [PUBLIC_TAB_CODES.EQUIPOS]: {
      value: PUBLIC_TAB_CODES.EQUIPOS,
      label: "Equipos",
      icon: <Diversity3Icon />,
      panel: renderEquiposPanel({ idinstitution, handleInstitutionClick }),
    },
    [PUBLIC_TAB_CODES.FIXTURE]: {
      value: PUBLIC_TAB_CODES.FIXTURE,
      label: "Fixture",
      icon: <SettingsInputSvideoIcon />,
      panel:
        Number(ideventNumber) === 16 ? (
          <BracketWrapper />
        ) : Number(ideventNumber) === 17 ? (
          <Event17PublicBracket
            idevent={ideventNumber}
            idsport={idsportNumber}
          />
        ) : (
          <Fixture />
        ),
    },
    [PUBLIC_TAB_CODES.PROGRAMACION]: {
      value: PUBLIC_TAB_CODES.PROGRAMACION,
      label: "Programación",
      icon: <CalendarMonthIcon />,
      panel: renderProgramacionPanel({ isColectivoLike }),
    },
    [PUBLIC_TAB_CODES.RESULTADOS]: {
      value: PUBLIC_TAB_CODES.RESULTADOS,
      label: "Resultados",
      icon: <ScoreboardIcon />,
      panel: renderResultadosPanel({ isColectivoLike }),
    },
    [PUBLIC_TAB_CODES.ESTADISTICA]: {
      value: PUBLIC_TAB_CODES.ESTADISTICA,
      label: "Estadística",
      icon: <PollIcon />,
      panel: <Estadistica />,
    },
    [PUBLIC_TAB_CODES.INFORMACION]: {
      value: PUBLIC_TAB_CODES.INFORMACION,
      label: "Información",
      icon: <InfoIcon />,
      panel: <Informacion />,
    },
    [PUBLIC_TAB_CODES.PRUEBAS]: {
      value: PUBLIC_TAB_CODES.PRUEBAS,
      label: "Pruebas",
      icon: <MilitaryTechIcon />,
      panel: renderPruebasPanel({ idsportNumber, legacyContext }),
    },
    [PUBLIC_TAB_CODES.CATEGORIAS]: {
      value: PUBLIC_TAB_CODES.CATEGORIAS,
      label: "Categorías",
      icon: <MilitaryTechIcon />,
      panel: renderCategoriasPanel({ ideventNumber, idsportNumber, legacyContext }),
    },
    [PUBLIC_TAB_CODES.TABLA_FINAL]: {
      value: PUBLIC_TAB_CODES.TABLA_FINAL,
      label: "Tabla Final",
      icon: <ScoreboardIcon />,
      panel: renderTablaFinalPanel({ legacyContext }),
    },
    [PUBLIC_TAB_CODES.MEDALLERO]: {
      value: PUBLIC_TAB_CODES.MEDALLERO,
      label: "Medallero",
      icon: <MilitaryTechIcon />,
      panel: <Medallero />,
    },
  };
};

const buildTabsFromCodes = (codes, catalog) => {
  return codes.map((code) => catalog[String(code)]).filter(Boolean);
};

/**
 * Resuelve tabs finales para SportSection.
 *
 * - typeSport 1: fijo => Equipos, Fixture, Programación, Resultados, Estadística.
 * - typeSport 2: fijo => Equipos, Programación, Resultados, Estadística.
 * - typeSport 3: configurable desde tabs_config_publico; fallback individual fijo.
 *
 * IMPORTANTE:
 * Las condiciones legacy como Atletismo/Natación/Judo/Lucha/Karate/Event 80
 * NO agregan tabs automáticamente. Solo afectan qué panel se renderiza cuando
 * el código del tab fue habilitado desde config_category.tabs_config_publico.
 */
export const buildSportSectionTabs = ({
  sportType,
  ideventNumber,
  idsportNumber,
  idinstitution,
  handleInstitutionClick,
  tabsConfigPublico,
}) => {
  const catalog = buildPublicTabCatalog({
    sportType,
    ideventNumber,
    idsportNumber,
    idinstitution,
    handleInstitutionClick,
  });

  if (Number(sportType) === SPORT_TYPES.COLECTIVO) {
    return buildTabsFromCodes(DEFAULT_PUBLIC_TAB_CODES.COLECTIVO, catalog);
  }

  if (Number(sportType) === SPORT_TYPES.COLECTIVO_EXPRESS) {
    return buildTabsFromCodes(
      DEFAULT_PUBLIC_TAB_CODES.COLECTIVO_EXPRESS,
      catalog
    );
  }

  if (Number(sportType) === SPORT_TYPES.INDIVIDUAL) {
    const individualCodes = parseTabsConfigPublico(
      tabsConfigPublico,
      DEFAULT_PUBLIC_TAB_CODES.INDIVIDUAL
    );

    return buildTabsFromCodes(individualCodes, catalog);
  }

  return buildTabsFromCodes(DEFAULT_PUBLIC_TAB_CODES.COLECTIVO_EXPRESS, catalog);
};
